import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface TabConfig {
  id: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  badge?: string | number;
}

export interface ActionButtonConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  className?: string;
}

export interface HeaderProps {
  // Titre et description
  title: string;
  subtitle?: string;
  
  // Configuration des onglets
  tabs?: TabConfig[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  
  // Configuration des boutons d'action
  actions?: ActionButtonConfig[];
  
  // Contenu personnalisé
  customContent?: React.ReactNode;
  
  // Classes personnalisées
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  actionsContainerClassName?: string;
}

const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  tabs = [],
  activeTab,
  onTabChange,
  actions = [],
  customContent,
  className = '',
  titleClassName = '',
  subtitleClassName = '',
  actionsContainerClassName = '',
}) => {
  // Classes de variante pour les boutons
  const getButtonVariantClasses = (variant?: string) => {
    switch (variant) {
      case 'secondary':
        return 'bg-gray-600 text-white hover:bg-gray-700';
      case 'danger':
        return 'bg-red-600 text-white hover:bg-red-700';
      case 'success':
        return 'bg-emerald-600 text-white hover:bg-emerald-700';
      case 'primary':
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700';
    }
  };

  return (
    <div className={`mb-6 ${className}`}>
      {/* Section titre et actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Titre et sous-titre */}
        <div className="flex-1">
          <h1 className={`text-2xl md:text-3xl font-bold text-gray-900 ${titleClassName}`}>
            {title}
          </h1>
          {subtitle && (
            <p className={`text-gray-600 mt-1 ${subtitleClassName}`}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Boutons d'action */}
        {actions.length > 0 && (
          <div className={`flex items-center gap-2 ${actionsContainerClassName}`}>
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={action.onClick}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl ${getButtonVariantClasses(action.variant)} ${action.className || ''}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Contenu personnalisé (optionnel) */}
      {customContent && (
        <div className="mb-6">
          {customContent}
        </div>
      )}

      {/* Onglets (optionnel) */}
      {tabs.length > 0 && activeTab && onTabChange && (
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {Icon && <Icon className="w-4 h-4" />}
                      <span>
                        {tab.label}
                        {(tab.count !== undefined || tab.badge) && (
                          <span className="ml-2 text-gray-500">
                            ({tab.count ?? tab.badge})
                          </span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;